import { Card, CardContent, Typography, Box, useTheme, Tooltip } from '@mui/material';
import type { TopClienteItem } from '@azoupvendas/shared';

interface Props {
  dados: TopClienteItem[];
}

function formatCurrency(value: number): string {
  if (value >= 1_000_000)
    return value.toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      maximumFractionDigits: 0
    });
  return value.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 0
  });
}

// AIDEV-NOTE: Lista ranqueada com barras de progresso — melhor UX que gráfico horizontal com nomes longos
export function TopClientesChart({ dados }: Props) {
  const theme = useTheme();

  const sorted = [...dados].sort((a, b) => b.total - a.total);
  const maxTotal = sorted[0]?.total ?? 1;

  if (sorted.length === 0) {
    return (
      <Card>
        <CardContent>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            Top 10 clientes
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Nenhum pedido no período
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent>
        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
          Top 10 clientes
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
          {sorted.map((cliente, index) => {
            const pct = (cliente.total / maxTotal) * 100;
            const isTop3 = index < 3;
            return (
              <Box key={index}>
                {/* Linha com rank + nome + valor */}
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 0.4 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      minWidth: 20,
                      fontWeight: 700,
                      color: isTop3 ? theme.palette.primary.main : 'text.disabled'
                    }}
                  >
                    {index + 1}
                  </Typography>
                  <Tooltip title={cliente.nome} placement="top-start" enterDelay={400}>
                    <Typography
                      variant="body2"
                      fontWeight={isTop3 ? 600 : 400}
                      sx={{
                        flex: 1,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        maxWidth: '100%'
                      }}
                    >
                      {cliente.nome}
                    </Typography>
                  </Tooltip>
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    sx={{ whiteSpace: 'nowrap', color: isTop3 ? 'text.primary' : 'text.secondary' }}
                  >
                    {formatCurrency(cliente.total)}
                  </Typography>
                </Box>
                {/* Barra de progresso */}
                <Box sx={{ height: 4, borderRadius: 2, bgcolor: 'grey.100', overflow: 'hidden' }}>
                  <Box
                    sx={{
                      height: '100%',
                      width: `${pct}%`,
                      borderRadius: 2,
                      bgcolor: isTop3 ? theme.palette.primary.main : theme.palette.primary.light,
                      transition: 'width 0.4s ease'
                    }}
                  />
                </Box>
              </Box>
            );
          })}
        </Box>
      </CardContent>
    </Card>
  );
}
